import { type MoneyAmount } from 'types'
import { getMinifiedLocaleCurrencyStringFromMoneyAmount } from 'utils'

import { type MarketplaceProduct, type MarketplaceProductCategory, type MarketplaceProductResponse, type MarketplaceProductVariant, type SessionContextType } from './types'

const formatCurrency = (amount?: MoneyAmount | null) => amount ? getMinifiedLocaleCurrencyStringFromMoneyAmount(amount) : undefined

const compactPayload = (payload: Record<string, unknown>) => Object.fromEntries(
  Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== '')
)

const getBaseMemberPayload = (member: SessionContextType) => compactPayload({
  userId: member.id ?? undefined,
  member_id: member.id ?? undefined,
  employer_id: member.employerId ?? undefined,
  auth_status: member.isAuthenticated ? 'authenticated' : 'unauthenticated',
  experience: member.publicView ? 'public' : 'member',
})

const getBalancePayload = (member: SessionContextType) => (
  member.balance ? { balance: formatCurrency(member.balance) } : {}
)

const getReferrerPayload = () => ({
  referrer_page: document.referrer,
})

const getBaseProductPayload = (product: MarketplaceProduct | MarketplaceProductResponse) => ({
  product_id: product.id,
  product_name: product.name,
  product_brand: product.product_brand,
  merchant_id: product.marketplace_merchant_id,
  merchant_name: product.marketplace_merchant_name,
})

const getBaseVariantPayload = (variant: MarketplaceProductVariant) => ({
  variant_id: variant.id,
  variant_name: variant.name,
  base_price: formatCurrency(variant.base_price),
  discounted_price: formatCurrency(variant.discounted_price),
})

export const emitProductsViewEvent = (member: SessionContextType) => window.segment?.page('[Marketplace] Products', {
  ...getReferrerPayload(),
  ...getBaseMemberPayload(member),
  ...getBalancePayload(member),
})

export const emitProductRequestSubmittedEvent = (member: SessionContextType, productName: string) => window.segment?.track('[Marketplace] Product request submitted', {
  ...getReferrerPayload(),
  ...getBaseMemberPayload(member),
  ...getBalancePayload(member),
  product_name: productName,
})

export const emitProductsExitEvent = (member: SessionContextType, durationSeconds: number) => {
  window.segment?.track('[Marketplace] Products page exited', {
    ...getReferrerPayload(),
    ...getBaseMemberPayload(member),
    time_on_page_s: durationSeconds,
  })
}

export const emitBalancePillClickedEvent = (member: SessionContextType) => window.segment?.track('[Marketplace] Balance clicked', {
  ...getBaseMemberPayload(member),
  ...getBalancePayload(member),
})

export const emitCategorySelectedEvent = (member: SessionContextType, category: MarketplaceProductCategory, categories: MarketplaceProductCategory[], categoryHistory: MarketplaceProductCategory[]) => window.segment?.track('[Marketplace] Product category selected', {
  ...getBaseMemberPayload(member),
  category,
  category_history: categoryHistory,
  all_categories: categories,
  category_position: categories.findIndex(c => c === category),
})

export const emitProductClickedEvent = (member: SessionContextType, product: MarketplaceProductResponse, position: number, activeCategories: MarketplaceProductCategory[]) => window.segment?.track('[Marketplace] Product card clicked', {
  ...getBaseMemberPayload(member),
  ...getBalancePayload(member),
  ...getBaseProductPayload(product),
  base_price: product.base_price?.formatted_amount,
  discounted_price: product.discounted_price?.formatted_amount,
  position,
  from_categories: activeCategories,
})

export const emitProductViewEvent = (member: SessionContextType, product: MarketplaceProduct, selectedVariant: MarketplaceProductVariant) => window.segment?.page('[Marketplace] Product', {
  ...getReferrerPayload(),
  ...getBaseMemberPayload(member),
  ...getBalancePayload(member),
  ...getBaseProductPayload(product),
  variant_count: product.marketplace_product_variants.length,
  default_variant_id: selectedVariant.id,
  default_variant_name: selectedVariant.name,
  default_variant_price: formatCurrency(selectedVariant.base_price),
  default_variant_discounted_price: formatCurrency(selectedVariant.discounted_price),
})

export const emitBackClickEvent = (member: SessionContextType, product: MarketplaceProduct) => {
  window.segment?.track('[Marketplace] Back to marketplace clicked', {
    ...getReferrerPayload(),
    ...getBaseMemberPayload(member),
    ...getBaseProductPayload(product),
    from_product_id: product.id,
    from_product_name: product.name,
  })
}

export const emitSelectVariantEvent = (member: SessionContextType, product: MarketplaceProduct, variant: MarketplaceProductVariant) => {
  window.segment?.track('[Marketplace] Variant selected', {
    ...getBaseMemberPayload(member),
    ...getBalancePayload(member),
    ...getBaseProductPayload(product),
    ...getBaseVariantPayload(variant),
    variant_position: product.marketplace_product_variants.findIndex(v => v.id === variant.id),
  })
}

export const emitPurchaseModalOpened = (member: SessionContextType, product: MarketplaceProduct, selectedVariant: MarketplaceProductVariant, timeToPurchase: number) => {
  window.segment?.track('[Marketplace] Product purchase modal opened', {
    ...getReferrerPayload(),
    ...getBaseMemberPayload(member),
    ...getBalancePayload(member),
    ...getBaseProductPayload(product),
    ...getBaseVariantPayload(selectedVariant),
    time_to_modal_open_s: timeToPurchase,
    categories: product.categories,
    redirect_uri: selectedVariant.redirect_uri,
  })
}

export const emitPurchaseModalClosed = (member: SessionContextType, product: MarketplaceProduct, selectedVariant: MarketplaceProductVariant) => {
  window.segment?.track('[Marketplace] Product purchase modal closed', {
    ...getReferrerPayload(),
    ...getBaseMemberPayload(member),
    ...getBalancePayload(member),
    ...getBaseProductPayload(product),
    ...getBaseVariantPayload(selectedVariant),
    categories: product.categories,
    redirect_uri: selectedVariant.redirect_uri,
  })
}

export const emitThatchTermsViewed = (member: SessionContextType, product: MarketplaceProduct, selectedVariant: MarketplaceProductVariant) => {
  window.segment?.track('[Marketplace] Thatch terms viewed', {
    ...getReferrerPayload(),
    ...getBaseMemberPayload(member),
    ...getBalancePayload(member),
    ...getBaseProductPayload(product),
    ...getBaseVariantPayload(selectedVariant),
    categories: product.categories,
    redirect_uri: selectedVariant.redirect_uri,
  })
}

export const emitMarketplaceDisclosureViewed = (member: SessionContextType, product: MarketplaceProduct, selectedVariant: MarketplaceProductVariant) => {
  window.segment?.track('[Marketplace] Marketplace disclosure viewed', {
    ...getReferrerPayload(),
    ...getBaseMemberPayload(member),
    ...getBalancePayload(member),
    ...getBaseProductPayload(product),
    ...getBaseVariantPayload(selectedVariant),
    categories: product.categories,
    redirect_uri: selectedVariant.redirect_uri,
  })
}

export const emitPurchaseClickedEvent = (member: SessionContextType, product: MarketplaceProduct, selectedVariant: MarketplaceProductVariant, timeToPurchase: number) => {
  window.segment?.track('[Marketplace] Product purchase initiated', {
    ...getReferrerPayload(),
    ...getBaseMemberPayload(member),
    ...getBalancePayload(member),
    ...getBaseProductPayload(product),
    ...getBaseVariantPayload(selectedVariant),
    time_to_purchase_s: timeToPurchase,
    categories: product.categories,
    redirect_uri: selectedVariant.redirect_uri,
  })
}

export const emitProductExitEvent = (member: SessionContextType, product: MarketplaceProduct, durationSeconds: number) => {
  window.segment?.track('[Marketplace] Product page exited', {
    ...getReferrerPayload(),
    ...getBaseMemberPayload(member),
    ...getBaseProductPayload(product),
    time_on_page_s: durationSeconds,
    categories: product.categories,
  })
}

// Hosted checkout events
export const emitCheckoutViewEvent = (member: SessionContextType, product: MarketplaceProduct, selectedVariant: MarketplaceProductVariant) => {
  window.segment?.page('[Marketplace] Checkout', {
    ...getReferrerPayload(),
    ...getBaseMemberPayload(member),
    ...getBalancePayload(member),
    ...getBaseProductPayload(product),
    ...getBaseVariantPayload(selectedVariant),
    categories: product.categories,
  })
}

export const emitCheckoutBuyClickedEvent = (member: SessionContextType, product: MarketplaceProduct, selectedVariant: MarketplaceProductVariant, timeOnPage: number) => {
  window.segment?.track('[Marketplace] Checkout buy clicked', {
    ...getReferrerPayload(),
    ...getBaseMemberPayload(member),
    ...getBalancePayload(member),
    ...getBaseProductPayload(product),
    ...getBaseVariantPayload(selectedVariant),
    time_to_buy_s: timeOnPage,
    categories: product.categories,
  })
}

export const emitCheckoutExitEvent = (member: SessionContextType, product: MarketplaceProduct, selectedVariant: MarketplaceProductVariant, durationSeconds: number) => {
  window.segment?.track('[Marketplace] Checkout page exited', {
    ...getReferrerPayload(),
    ...getBaseMemberPayload(member),
    ...getBaseProductPayload(product),
    ...getBaseVariantPayload(selectedVariant),
    time_on_page_s: durationSeconds,
    categories: product.categories,
  })
}
